import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { UserRole } from "../types/index";
import type { User, LoginRequest, AuthContextType } from "../types/index";
import apiClient from '../services/api';

// Создаем контекст
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Хук для использования контекста
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Провайдер контекста
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Проверяем токен при загрузке приложения
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('access_token');
      if (storedToken) {
        try {
          apiClient.setToken(storedToken);
          const userData = await apiClient.getCurrentUser();
          setUser(userData);
          setToken(storedToken);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Authentication failed:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          apiClient.clearToken();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Функция входа
  const login = async (credentials: LoginRequest) => {
    try {
      const response = await apiClient.login(credentials);
      setUser(response.user);
      setToken(response.access_token);
      setIsAuthenticated(true);
      
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      apiClient.setToken(response.access_token);
      
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  // Функция выхода
  const logout = async () => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      apiClient.clearToken();
    }
  };

  // Проверка аутентификации
  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiClient.refreshToken();
      setToken(response.access_token);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      apiClient.setToken(response.access_token);
      
      return response;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  };

  const contextValue: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Хуки для проверки ролей и прав доступа
export const useRole = () => {
  const { user } = useAuth();

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const canManageUsers = (): boolean => {
    return hasRole(UserRole.ADMIN);
  };

  const canManageDevices = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.ENGINEER]);
  };

  const canManageSettings = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER]);
  };

  const canViewReports = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.DIRECTOR]);
  };

  // Новые хуки для медиафайлов
  const canUploadMedia = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER]);
  };

  const canEditMedia = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER]);
  };

  const canDeleteMedia = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER]);
  };

  const canViewMedia = (): boolean => {
    return hasAnyRole([
      UserRole.ADMIN,
      UserRole.MANAGER,
      UserRole.SCHEDULER,
      UserRole.VIEWER,
    ]);
  };

  // Хуки для плейлистов
  const canCreatePlaylists = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER]);
  };

  const canEditPlaylists = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER]);
  };

  const canDeletePlaylists = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER]);
  };

  const canManagePlaylists = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER]);
  };

  // Хуки для расписания
  const canCreateSchedules = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER]);
  };

  const canEditSchedules = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER]);
  };

  const canDeleteSchedules = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER]);
  };

  const canManageSchedules = (): boolean => {
    return hasAnyRole([UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER]);
  };

  return {
    hasRole,
    hasAnyRole,
    canManageUsers,
    canManageDevices,
    canManageSettings,
    canViewReports,
    // Медиафайлы
    canUploadMedia,
    canEditMedia,
    canDeleteMedia,
    canViewMedia,
    // Плейлисты
    canCreatePlaylists,
    canEditPlaylists,
    canDeletePlaylists,
    canManagePlaylists,
    // Расписание
    canCreateSchedules,
    canEditSchedules,
    canDeleteSchedules,
    canManageSchedules,
  };
};

export default AuthContext; 